import { Injectable } from '@angular/core';
import { ShapeModel } from '../../../base/shape/model/shape.mdl';
import { AbstractCommand, Command, CommandService, ModalController, StateService } from 'flux-core';
import { IDialogBoxData, PlanPermission } from 'flux-definition';
import { UpgradeDialogType, UpgradeDialogWindow } from 'flux-user';
import { LibraryList } from '../../ui/temp-add-libs-menu/library-list';
import { DiagramLocatorLocator } from '../../../base/diagram/locator/diagram-locator-locator';
import { DefinitionLocator } from '../../../base/shape/definition/definition-locator.svc';
import { fromPromise } from 'rxjs/internal-compatibility';
import { PlanPermManager } from '../../../base/permission/plan-perm-manager';

@Injectable()
@Command()
export class CheckPremiumShapeLimitations extends AbstractCommand {

    public data: {
        shapes: { [key: string]: ShapeModel },
    };

    public static readonly maxPremiumShapesCount = 10;

    constructor(
        protected libraryList: LibraryList,
        protected planPermManager: PlanPermManager,
        protected modalController: ModalController,
        protected state: StateService<any, any>,
        protected ll: DiagramLocatorLocator,
        protected defL: DefinitionLocator,
        protected commandService: CommandService,
    ) {
        super();
    }

    public execute() {
        return fromPromise( this.executePromise());
    }

    protected async executePromise(): Promise<boolean> {
        const shapes = this.data.shapes;
        let premiumShapesCount = 0;
        let addedPremiumShapesCount = 0;
        let premiumShapesCountLoaded = false;
        const hasPremiumLibraryAccess = this.planPermManager.check([ PlanPermission.CREATELY_PREMIUM_LIBRARIES ]);
        for ( const shapeId in shapes ) {
            const currentShape = shapes[shapeId];
            const currentShapeLibraryId = currentShape.defId.split( '.' )[1];
            const isPremiumShapeInDef = await this.isPremiumShapeInDef( currentShape );
            const isPremiumShape =  isPremiumShapeInDef || this.libraryList.belongsToPremiumGroup( currentShapeLibraryId );
            currentShape.premium = isPremiumShape;
            if ( isPremiumShape && !hasPremiumLibraryAccess ) {
                if ( !premiumShapesCountLoaded ) {
                    premiumShapesCount = await this.getPremiumShapesCount();
                    premiumShapesCountLoaded = true;
                }

                if ( premiumShapesCount + addedPremiumShapesCount >= CheckPremiumShapeLimitations.maxPremiumShapesCount ) {
                    this.showUpgradeDialog( this.getUpgradeDialogOptions( currentShapeLibraryId, currentShape.defId ));
                    throw new Error( 'Permission is denied' );
                }

                addedPremiumShapesCount++;
            }
        }
        this.resultData = this.data;
        return true;
    }

    protected getUpgradeDialogOptions( shapeLibraryId: string, shapeDefId: string ): { shapeLibraryId?: string; shapeDefId?: string } {
        if ( shapeLibraryId === 'genogram' ) {
            return { shapeDefId };
        }
        return { shapeLibraryId };
    }

    protected async getPremiumShapesCount(): Promise<number> {
        const currentDiagram = await this.ll.forCurrent( false ).getDiagramOnce().toPromise();
        const result = await this.commandService.execute( 'GetPremiumShapeUsage', undefined, {
            diagramId: currentDiagram.id,
        }).toPromise();
        const otherDiagramsPremiumShapesCount = this.getPremiumShapesCountFromResult( result );
        return otherDiagramsPremiumShapesCount + currentDiagram.premiumShapesCount;
    }

    protected getPremiumShapesCountFromResult( result: any ): number {
        const resultData = result?.resultData;
        const premiumShapesCount = Array.isArray( resultData )
            ? resultData[0]?.premiumShapesCount
            : resultData?.premiumShapesCount;
        return Number( premiumShapesCount ) || 0;
    }

    protected async isPremiumShapeInDef( shape ) {
        const def = await this.defL.getDefinition( shape.defId, shape.version ).toPromise();
        return ( def as any ).premium;
    }

    /**
     * Showing the upgradeDialog
     */
    protected showUpgradeDialog(opts: { shapeLibraryId?: string; shapeDefId?: string } = {}) {
        const dialogData = {
            id: PlanPermission.CREATELY_PREMIUM_LIBRARIES,
            iframeType: UpgradeDialogType.PremiumShape,
            buttons: [
                {
                    type: 'upgrade',
                    clickHandler: () => {},
                },
            ],
            integrationContext: {
                embedded: this.state.get( 'ApplicationIsEmbedded' ),
                environment: this.state.get( 'PluginApp' ),
            },
            shapeLibraryId: opts.shapeLibraryId,
            shapeDefId: opts.shapeDefId,
        } as IDialogBoxData;

        this.modalController.show( UpgradeDialogWindow, {
            inputs: {
                dialogData,
            },
        });
    }

}

Object.defineProperty( CheckPremiumShapeLimitations, 'name', {
    value: 'CheckPremiumShapeLimitations',
});
